import { To } from 'react-router-dom';
import { TopLevelCategoryBasePath } from '../constants';
import {
  AutocompleteResponseModel,
  AutocompleteSuggestionItem,
} from '../models/Search';
import { MyBenefitsTabTypes } from '../Pages/MyBenefitsPage/types';
import { TopLevelCategories } from '../enums';

export type AutocompleteNavigationTarget = {
  to: To;
  state?: {
    tab?: MyBenefitsTabTypes;
    activeBenefitId?: number;
  };
};

export const flattenAutocompleteSuggestions = (
  suggestions: AutocompleteResponseModel | null,
): AutocompleteSuggestionItem[] => {
  if (!suggestions) {
    return [];
  }

  return [
    ...suggestions.brands.map(item => ({
      ...item,
      type: 'brand' as const,
    })),

    ...suggestions.products.map(item => ({
      ...item,
      type: 'product' as const,
    })),

    ...suggestions.categories.map(item => ({
      ...item,
      type: 'category' as const,
    })),
  ];
};

export const hasAutocompleteSuggestions = (
  suggestions: AutocompleteResponseModel | null | undefined,
) => {
  if (!suggestions) {
    return false;
  }

  return (
    (suggestions.brands?.length ?? 0) > 0 ||
    (suggestions.products?.length ?? 0) > 0 ||
    (suggestions.categories?.length ?? 0) > 0
  );
};

export const getAutocompleteSuggestionTitle = (
  suggestion: AutocompleteSuggestionItem,
) => {
  switch (suggestion.type) {
    case 'brand':
      return suggestion.brandName;

    case 'product':
      return suggestion.productName;

    case 'category':
      return suggestion.categoryName;

    default:
      return '';
  }
};

export const getAutocompleteSuggestionSubtitle = (
  suggestion: AutocompleteSuggestionItem,
) => {
  switch (suggestion.type) {
    case 'product':
      return suggestion.categoryName ? `in ${suggestion.categoryName}` : '';

    case 'category':
      return suggestion.topLevelCategoryName
        ? `in ${suggestion.topLevelCategoryName}`
        : '';

    default:
      return '';
  }
};

export const getAutocompleteSuggestionNavigationTarget = (
  suggestion: AutocompleteSuggestionItem,
): AutocompleteNavigationTarget => {
  switch (suggestion.type) {
    case 'brand':
      return {
        to: `/products/search?brand=${suggestion.brandId}_${encodeURIComponent(
          suggestion.brandName,
        )}`,
      };

    case 'product':
      return {
        to: `/products/${suggestion.productId}`,
      };

    case 'category': {
      const topLevelCategoryId = Number(suggestion.topLevelCategoryId);

      if (topLevelCategoryId === TopLevelCategories.FixedBenefits) {
        return {
          to: '/my-account/my-benefits',
          state: {
            tab: MyBenefitsTabTypes.FixedBenefits,
            activeBenefitId: Number(suggestion.categoryId),
          },
        };
      }

      const basePath = TopLevelCategoryBasePath[topLevelCategoryId];

      return {
        to: basePath
          ? `${basePath}/${suggestion.categoryId}`
          : `/category/${suggestion.categoryId}`,
      };
    }

    default:
      return {
        to: '/',
      };
  }
};

export const getAutocompleteSuggestionsCount = (
  suggestions: AutocompleteResponseModel | null | undefined,
): number => {
  if (!suggestions) {
    return 0;
  }

  return (
    (suggestions.brands?.length ?? 0) +
    (suggestions.products?.length ?? 0) +
    (suggestions.categories?.length ?? 0)
  );
};
